import {
  AlertTriangle,
  ArrowRight,
  Check,
  Clock3,
  Hammer,
  HardHat,
  HeartPulse,
  Moon,
  Plus,
  Power,
  Recycle,
  UserRound,
  UsersRound,
  Wrench,
  XCircle,
} from "lucide-react";
import { useEffect, useId, useMemo, useState } from "react";

import { formatDuration, formatQuantity } from "../lib/format";
import { spaceFootprint } from "../lib/bunkerGeometry";
import {
  coverageImpactWarning,
  isSurvivorAvailableForTemporaryWork,
  roomLabourDefaults,
  roomStaffingProjection,
  survivorActivityLabel,
  survivorDutySpaceId,
} from "../lib/scheduling";
import type { Catalog, GameCommand, GameState, InstallationLevel, LabourPriority, PowerPriority, ResourceKey, RoomDefinition, RoomTier, Space, SpaceFootprint, Survivor } from "../types";
import { SurvivorPortrait } from "./SurvivorPortrait";

interface InspectorProps {
  state: GameState;
  catalog: Catalog;
  space: Space;
  busy: boolean;
  onCommand: (command: GameCommand) => Promise<unknown>;
}

const LEGACY_MINIMUM_UNITS: Record<RoomDefinition["size"], number> = {
  small: 1,
  medium: 2,
  large: 4,
};

export function structuralUnits(space: Space): number {
  const footprint = spaceFootprint(space);
  return footprint.width * footprint.height;
}

export function roomTierForSpace(space: Space): RoomTier {
  if (space.room?.footprint) return roomTierForFootprint(space.room.footprint);
  if (space.room?.tier) return space.room.tier;
  const units = space.room?.units ?? structuralUnits(space);
  if (units >= 4) return "expanded";
  if (units >= 2) return "standard";
  return "compact";
}

export function roomMinimumUnits(definition: RoomDefinition): number {
  return definition.minimum_units ?? LEGACY_MINIMUM_UNITS[definition.size] ?? 1;
}

export function roomWorkerCapacity(definition: RoomDefinition, tier: RoomTier): number {
  return definition.workers_by_tier?.[tier] ?? definition.workers;
}

export function roomTierForFootprint(footprint: SpaceFootprint): RoomTier {
  const units = footprint.width * footprint.height;
  return units >= 4 ? "expanded" : units >= 2 ? "standard" : "compact";
}

export function footprintLabel(footprint: SpaceFootprint): string {
  const tier = roomTierForFootprint(footprint);
  const orientation = footprint.width === 2 && footprint.height === 1
    ? " wide"
    : footprint.width === 1 && footprint.height === 2
      ? " tall"
      : "";
  return `${tier[0]!.toUpperCase()}${tier.slice(1)}${orientation} ${footprint.width} × ${footprint.height}`;
}

function footprintKey(footprint: SpaceFootprint): string {
  return `${footprint.width}x${footprint.height}`;
}

function legacyBlueprints(definition: RoomDefinition): SpaceFootprint[] {
  const minimum = roomMinimumUnits(definition);
  return [
    { width: 1, height: 1 },
    { width: 2, height: 1 },
    { width: 1, height: 2 },
    { width: 2, height: 2 },
  ].filter((footprint) => footprint.width * footprint.height >= minimum);
}

export function availableBlueprints(definition: RoomDefinition, space: Space): SpaceFootprint[] {
  const capacity = spaceFootprint(space);
  return (definition.allowed_footprints?.length ? definition.allowed_footprints : legacyBlueprints(definition))
    .filter((footprint) => footprint.width <= capacity.width && footprint.height <= capacity.height)
    .sort((left, right) =>
      left.width * left.height - right.width * right.height
      || right.width - left.width
      || left.height - right.height,
    );
}

function buildCost(definition: RoomDefinition, footprint: SpaceFootprint): Partial<Record<ResourceKey, number>> {
  const tier = roomTierForFootprint(footprint);
  const authored = definition.build_cost_by_tier?.[tier];
  if (authored) return authored;
  const multiplier = definition.construction_cost_multipliers?.[tier] ?? 1;
  return Object.fromEntries(
    (Object.entries(definition.cost) as Array<[ResourceKey, number]>).map(([key, amount]) => [key, Math.round(amount * multiplier)]),
  );
}

function buildSeconds(definition: RoomDefinition, footprint: SpaceFootprint): number {
  const tier = roomTierForFootprint(footprint);
  return definition.build_seconds_by_tier?.[tier]
    ?? Math.round(definition.build_seconds * (definition.construction_time_multipliers?.[tier] ?? 1));
}

export function incrementalCost(
  definition: RoomDefinition,
  current: SpaceFootprint,
  target: SpaceFootprint,
  installationLevel: InstallationLevel = 1,
): Partial<Record<ResourceKey, number>> {
  const totalInvestment = (footprint: SpaceFootprint) => {
    const tier = roomTierForFootprint(footprint);
    const total = { ...buildCost(definition, footprint) };
    for (let level = 2; level <= installationLevel; level += 1) {
      const upgrade = definition.upgrade_cost_by_tier_and_level?.[tier]?.[level as InstallationLevel] ?? {};
      for (const [resource, amount] of Object.entries(upgrade) as Array<[ResourceKey, number]>) {
        total[resource] = (total[resource] ?? 0) + amount;
      }
    }
    return total;
  };
  const existing = totalInvestment(current);
  return Object.fromEntries(
    (Object.entries(totalInvestment(target)) as Array<[ResourceKey, number]>)
      .map(([key, amount]): [ResourceKey, number] => [key, Math.max(0, amount - (existing[key] ?? 0))])
      .filter(([, amount]) => amount > 0),
  );
}

export function incrementalWorkSeconds(
  definition: RoomDefinition,
  current: SpaceFootprint,
  target: SpaceFootprint,
  installationLevel: InstallationLevel,
): number {
  const totalWork = (footprint: SpaceFootprint) => {
    const tier = roomTierForFootprint(footprint);
    let seconds = buildSeconds(definition, footprint);
    for (let level = 2; level <= installationLevel; level += 1) {
      seconds += definition.upgrade_seconds_by_tier_and_level?.[tier]?.[level as InstallationLevel] ?? 0;
    }
    return seconds;
  };
  return Math.max(30, totalWork(target) - totalWork(current));
}

function FootprintFacts({ space, footprint: built, tier, benefit, workers }: { space: Space; footprint?: SpaceFootprint; tier?: RoomTier; benefit?: string; workers?: number }) {
  const capacity = spaceFootprint(space);
  const footprint = built ?? capacity;
  const units = footprint.width * footprint.height;
  return (
    <>
      <dl className="space-facts room-scale-facts">
        <div><dt>{built ? "Room footprint" : "Chamber capacity"}</dt><dd>{footprint.width} × {footprint.height} · {units} {units === 1 ? "bay" : "bays"}</dd></div>
        {built && (built.width !== capacity.width || built.height !== capacity.height) && <div><dt>Reserved chamber</dt><dd>{capacity.width} × {capacity.height}</dd></div>}
        {tier && <div><dt>Room tier</dt><dd>{tier}</dd></div>}
        {workers !== undefined && <div><dt>Useful worker capacity</dt><dd>{workers}</dd></div>}
      </dl>
      {benefit && <p className="tier-benefit"><strong>{tier} benefit</strong>{benefit}</p>}
    </>
  );
}

function WorkerPicker({ state, survivors, selected, skill, max, onChange }: { state: GameState; survivors: Survivor[]; selected: string[]; skill: string; max?: number; onChange: (ids: string[]) => void }) {
  const warningIdPrefix = useId();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const matches = [...survivors]
    .filter((survivor) => `${survivor.name} ${survivor.role}`.toLowerCase().includes(search.trim().toLowerCase()))
    .sort((left, right) => (
      Number(selected.includes(right.id)) - Number(selected.includes(left.id))
      || (right.skills[skill as keyof Survivor["skills"]] ?? 0) - (left.skills[skill as keyof Survivor["skills"]] ?? 0)
      || left.name.localeCompare(right.name)
    ));
  const totalPages = Math.max(1, Math.ceil(matches.length / pageSize));
  const visible = matches.slice((page - 1) * pageSize, page * pageSize);
  useEffect(() => setPage(1), [search, skill]);
  return (
    <fieldset className="worker-picker">
      <legend><UsersRound size={14} />Assign workers <span>{skill}</span></legend>
      {survivors.length > 8 && <label className="worker-search"><span className="visually-hidden">Search workers</span><input type="search" placeholder="Search available workers" value={search} onChange={(event) => setSearch(event.currentTarget.value)} /></label>}
      {visible.map((survivor) => {
        const checked = selected.includes(survivor.id);
        const unavailable = !checked && max !== undefined && selected.length >= max;
        const rating = survivor.skills?.[skill as keyof Survivor["skills"]] ?? 0;
        const proposedSelection = checked ? selected : [...selected, survivor.id];
        const coverageWarning = coverageImpactWarning(state, survivor, proposedSelection);
        const warningId = `${warningIdPrefix}-${survivor.id}`;
        return (
          <label key={survivor.id} className={`${checked ? "checked" : ""} ${unavailable ? "disabled" : ""} ${coverageWarning ? "has-coverage-impact" : ""}`} title={coverageWarning ?? undefined}>
            <input
              type="checkbox"
              checked={checked}
              disabled={unavailable}
              aria-describedby={coverageWarning ? warningId : undefined}
              onChange={() => onChange(checked ? selected.filter((id) => id !== survivor.id) : [...selected, survivor.id])}
            />
            <SurvivorPortrait survivor={survivor} detail="thumb" />
            <span><strong>{survivor.name}</strong><small>{survivor.role}</small></span>
            <em>{rating}/5</em>
            {checked && <Check size={14} />}
            {coverageWarning && <small className="coverage-impact" id={warningId}><AlertTriangle size={12} />Protected coverage may fall</small>}
          </label>
        );
      })}
      {totalPages > 1 && <nav className="worker-pages" aria-label="Worker pages"><button type="button" disabled={page === 1} onClick={() => setPage((current) => current - 1)}>Previous</button><span>{page}/{totalPages}</span><button type="button" disabled={page === totalPages} onClick={() => setPage((current) => current + 1)}>Next</button></nav>}
    </fieldset>
  );
}

function Cost({ cost, catalog, resources }: { cost: Partial<Record<ResourceKey, number>>; catalog: Catalog; resources: Record<ResourceKey, number> }) {
  return (
    <div className="cost-list">
      {(Object.entries(cost) as Array<[ResourceKey, number]>).map(([resource, amount]) => (
        <span className={resources[resource] < amount ? "missing" : ""} key={resource}>
          {catalog.resources[resource].name} <b>{formatQuantity(amount)}</b>
        </span>
      ))}
    </div>
  );
}

const installationNumerals: Record<InstallationLevel, string> = {
  1: "I",
  2: "II",
  3: "III",
  4: "IV",
};

export function installationLevelLabel(level: InstallationLevel): string {
  return `Level ${installationNumerals[level]}`;
}

export function SpaceInspector({ state, catalog, space, busy, onCommand }: InspectorProps) {
  const task = state.tasks.find((item) => item.space_id === space.id);
  const available = useMemo(
    () => state.survivors.filter(isSurvivorAvailableForTemporaryWork),
    [state.survivors],
  );
  const availableIds = useMemo(
    () => new Set(available.map((survivor) => survivor.id)),
    [available],
  );
  const availableForDuty = useMemo(
    () => state.survivors.filter((survivor) => survivor.alive && survivor.status === "home" && !survivorDutySpaceId(survivor)),
    [state.survivors],
  );
  const [workers, setWorkers] = useState<string[]>([]);
  const validWorkers = useMemo(
    () => workers.filter((survivorId) => availableIds.has(survivorId)),
    [availableIds, workers],
  );
  const [selectedBlueprints, setSelectedBlueprints] = useState<Record<string, string>>({});
  const [selectedExpansion, setSelectedExpansion] = useState<string>("");
  useEffect(() => {
    setWorkers(available.slice(0, 1).map((item) => item.id));
    setSelectedBlueprints({});
    setSelectedExpansion("");
  }, [space.id]);
  useEffect(() => {
    setWorkers((current) => {
      const next = current.filter((survivorId) => availableIds.has(survivorId));
      return next.length === current.length ? current : next;
    });
  }, [availableIds]);

  if (task) {
    const assigned = task.worker_ids.map((id) => state.survivors.find((item) => item.id === id)).filter(Boolean) as Survivor[];
    return (
      <aside id="space-inspector" className="inspector panel-surface">
        <div className="inspector-kicker"><Hammer size={14} />Work in progress</div>
        <h2>{task.decommission && task.room_type ? `Dismantling ${catalog.rooms[task.room_type]?.short_name ?? "room"}` : task.kind === "build" && task.room_type ? (catalog.rooms[task.room_type]?.name ?? "Room construction") : task.kind === "expand" && task.room_type ? `Expanding ${catalog.rooms[task.room_type]?.short_name ?? "room"}` : task.kind === "upgrade" && task.room_type ? `Upgrading ${catalog.rooms[task.room_type]?.short_name ?? "room"} to ${installationLevelLabel(task.target_installation_level ?? 2)}` : task.kind === "repair" ? "Emergency repairs" : task.kind === "maintenance" ? "Preventive maintenance" : task.kind === "clear" ? "Clearing ruined room" : "Excavating chamber"}</h2>
        <FootprintFacts
          space={space}
          footprint={task.target_footprint}
          tier={task.target_footprint ? roomTierForFootprint(task.target_footprint) : undefined}
        />
        <div className="large-timer"><Clock3 size={18} /><span>{formatDuration(task.seconds_left)}</span><small>estimated remaining</small></div>
        {assigned.length ? (
          <div className="assigned-workers">
            {assigned.map((survivor) => <span key={survivor.id}><SurvivorPortrait survivor={survivor} detail="thumb" />{survivor.name}</span>)}
          </div>
        ) : <p className="warning-copy"><AlertTriangle size={15} />Work is paused until somebody is assigned.</p>}
        {available.length > 0 && (
          <div className="quick-assign">
            <h3>Add a worker</h3>
            {available.slice(0, 3).map((survivor) => {
              const warning = coverageImpactWarning(state, survivor);
              return (
                <button key={survivor.id} disabled={busy} title={warning ?? undefined} onClick={() => onCommand({ type: "assign_task", task_id: task.id, survivor_id: survivor.id })}>
                  <Plus size={13} /><SurvivorPortrait survivor={survivor} detail="thumb" />{survivor.name}<em>{survivor.skills[task.skill]}/5</em>
                  {warning && <small className="coverage-impact-short"><AlertTriangle size={11} />Coverage risk</small>}
                </button>
              );
            })}
          </div>
        )}
        <button className="secondary-button destructive-button" disabled={busy} onClick={() => onCommand({ type: "cancel_task", task_id: task.id })}>
          <XCircle size={15} />Cancel work order
        </button>
        <p className="cancellation-note">Cancellation recovers 70% of materials not yet committed. Completed work and the rest of the supplies are lost.</p>
      </aside>
    );
  }

  if (space.room) {
    const definition = catalog.rooms[space.room.type] ?? catalog.rooms.entrance!;
    const tier = roomTierForSpace(space);
    const capacity = roomWorkerCapacity(definition, tier);
    const tierBenefit = definition.tier_effects?.[tier];
    const currentFootprint = space.room.footprint ?? spaceFootprint(space);
    const installationLevel = space.room.installation_level ?? 1;
    const expansionBlueprints = space.room.type === "entrance" ? [] : availableBlueprints(definition, space)
      .filter((footprint) => footprint.width * footprint.height > currentFootprint.width * currentFootprint.height);
    const expansionFootprint = expansionBlueprints.find((footprint) => footprintKey(footprint) === selectedExpansion)
      ?? expansionBlueprints[0];
    const expansionTier = expansionFootprint ? roomTierForFootprint(expansionFootprint) : undefined;
    const expansionCost = expansionFootprint ? incrementalCost(definition, currentFootprint, expansionFootprint, installationLevel) : {};
    const expansionSeconds = expansionFootprint ? incrementalWorkSeconds(definition, currentFootprint, expansionFootprint, installationLevel) : 0;
    const expansionAffordable = (Object.entries(expansionCost) as Array<[ResourceKey, number]>).every(
      ([resource, amount]) => state.resources[resource] >= amount,
    );
    const roomUnits = space.room.units ?? currentFootprint.width * currentFootprint.height;
    const missingCondition = Math.max(0, 100 - space.room.condition);
    const repairCost = {
      scrap: Math.max(250, 3 * missingCondition * roomUnits),
      components: Math.max(25, Math.ceil((missingCondition * roomUnits) / 3)),
    };
    const maintenanceCost = {
      scrap: Math.max(20, 2 * missingCondition * roomUnits),
      components: Math.max(5, Math.ceil((missingCondition * roomUnits) / 6)),
    };
    const repairAffordable = (Object.entries(repairCost) as Array<[ResourceKey, number]>).every(
      ([resource, amount]) => state.resources[resource] >= amount,
    );
    const maintenanceAffordable = (Object.entries(maintenanceCost) as Array<[ResourceKey, number]>).every(
      ([resource, amount]) => state.resources[resource] >= amount,
    );
    const operation = space.room.operation;
    const enabled = space.room.enabled ?? true;
    const priority = space.room.power_priority ?? definition.default_power_priority ?? "normal";
    const labourDefaults = roomLabourDefaults(space.room.type);
    const labourPriority = space.room.labour_priority ?? definition.default_labour_priority ?? labourDefaults.priority;
    const staffing = roomStaffingProjection(space.room);
    const minimumStaff = space.room.minimum_staff ?? definition.default_minimum_staff ?? staffing.minimum_staff;
    const autoCover = space.room.auto_cover ?? definition.default_auto_cover ?? true;
    const preferredWorkers = staffing.preferred_worker_ids
      .map((id) => state.survivors.find((item) => item.id === id))
      .filter(Boolean) as Survivor[];
    const reliefWorkers = staffing.relief_worker_ids
      .map((id) => state.survivors.find((item) => item.id === id))
      .filter(Boolean) as Survivor[];
    const activeWorkerIds = new Set(staffing.active_worker_ids);
    const nameList = (ids: string[]) => ids
      .map((id) => state.survivors.find((person) => person.id === id)?.name ?? "Unknown")
      .join(", ");
    const treatment = operation?.treatment;
    const admittedPatientIds = treatment?.admitted_patient_ids ?? [];
    const waitingPatientIds = treatment?.waiting_patient_ids ?? treatment?.patient_ids ?? [];
    const totalPatientCount = admittedPatientIds.length + waitingPatientIds.length;
    const supportsInstallations = Boolean(definition.installation_level_cap_by_tier);
    const installationCap = definition.installation_level_cap_by_tier?.[tier] ?? installationLevel;
    const targetInstallationLevel = Math.min(4, installationLevel + 1) as InstallationLevel;
    const upgradeCost = definition.upgrade_cost_by_tier_and_level?.[tier]?.[targetInstallationLevel] ?? {};
    const upgradeSeconds = definition.upgrade_seconds_by_tier_and_level?.[tier]?.[targetInstallationLevel];
    const upgradeAffordable = (Object.entries(upgradeCost) as Array<[ResourceKey, number]>).every(
      ([resource, amount]) => state.resources[resource] >= amount,
    );
    const workshopReady = (state.site?.spaces ?? [space]).some((candidate) => {
      if (candidate.room?.type !== "workshop" || candidate.room.status !== "operational" || candidate.room.enabled === false) return false;
      const candidateDefinition = catalog.rooms.workshop;
      const candidateTier = roomTierForSpace(candidate);
      const demand = candidate.room.operation?.power_demand ?? candidateDefinition?.power_demand_by_tier?.[candidateTier] ?? 0;
      return demand === 0 || candidate.room.operation?.powered === true;
    });
    const upgradeGate = targetInstallationLevel >= 4 && !(state.research?.completed ?? []).includes("salvage_fabrication")
      ? "Research Salvage fabrication before commissioning a Level IV installation."
      : targetInstallationLevel >= 3 && !workshopReady
        ? "An enabled, powered Workshop is required to begin this installation."
        : null;
    const blockedCopy: Record<string, string> = {
      disabled: "Switched off by standing order",
      load_shed: "Load shed by the current power priority",
      no_power: "No generator power available",
      no_fuel: "Generator has no usable fuel",
      no_medicine: "Medicine stores are below one care point",
      unattended: "Running unattended at emergency efficiency",
      unstaffed: "Waiting for an assigned worker",
      input_shortage: "Waiting for recipe inputs",
      output_full: "Output stores are full",
      room_damaged: "Room is out of service",
    };
    if (space.room.status === "trashed") {
      return (
        <aside id="space-inspector" className="inspector panel-surface">
          <div className="inspector-kicker"><AlertTriangle size={14} />Room destroyed</div>
          <h2>Ruined {definition.short_name}</h2>
          <p>The room and everything stored inside it are lost. Clear the wreckage to recover this chamber for rebuilding.</p>
          <FootprintFacts space={space} footprint={currentFootprint} tier={tier} benefit={tierBenefit} workers={capacity} />
          <WorkerPicker state={state} survivors={available} selected={validWorkers} skill="engineering" max={4} onChange={setWorkers} />
          {available.length === 0 && <p className="warning-copy"><AlertTriangle size={15} />Nobody is available to clear the wreckage.</p>}
          <button className="action-button" disabled={busy || validWorkers.length === 0} onClick={() => onCommand({ type: "clear_trashed_room", space_id: space.id, worker_ids: validWorkers })}>
            <HardHat size={16} />Clear ruined room <ArrowRight size={15} />
          </button>
        </aside>
      );
    }
    return (
      <aside id="space-inspector" className="inspector panel-surface">
        <div className="inspector-kicker"><Wrench size={14} />{space.room.status}</div>
        <h2>{definition.name}</h2>
        <p>{definition.description}</p>
        <FootprintFacts space={space} footprint={currentFootprint} tier={tier} benefit={tierBenefit} workers={capacity} />
        {supportsInstallations && <section className="installation-summary" aria-label={`${definition.short_name} installation level`}>
          <span><small>Installation</small><strong>{installationLevelLabel(installationLevel)}</strong></span>
          <span><small>Footprint ceiling</small><strong>{installationLevelLabel(installationCap)}</strong></span>
          <span><small>Output</small><strong>{definition.installation_output_percent_by_level?.[installationLevel] ?? 100}%</strong></span>
          <span><small>Wear</small><strong>{definition.installation_wear_percent_by_level?.[installationLevel] ?? 100}%</strong></span>
        </section>}
        <div className="condition-card">
          <span>Room condition</span><strong>{space.room.condition}%</strong>
          <i><b style={{ width: `${space.room.condition}%` }} /></i>
        </div>
        {space.room.status === "operational" && (
          <section className="room-operation" aria-label={`${definition.short_name} operating controls`}>
            <header>
              <span>
                <Power aria-hidden="true" />
                <strong>{enabled ? "Room enabled" : "Room disabled"}</strong>
                <small>
                  {operation?.blocked_reason
                    ? blockedCopy[operation.blocked_reason] ?? operation.blocked_reason
                    : operation?.powered
                      ? "Powered and ready"
                      : "No electrical load required"}
                </small>
              </span>
              <button
                type="button"
                className="operation-toggle"
                aria-pressed={enabled}
                disabled={busy}
                onClick={() => onCommand({ type: "set_room_operation", space_id: space.id, enabled: !enabled })}
              >
                {enabled ? "Switch off" : "Switch on"}
              </button>
            </header>
            <dl className="operation-facts">
              <div><dt>Electrical load</dt><dd>{operation?.power_demand ?? definition.power_demand_by_tier?.[tier] ?? 0}</dd></div>
              <div><dt>Condition efficiency</dt><dd>{operation?.condition_efficiency_percent ?? 100}%</dd></div>
              <div><dt>Wear</dt><dd>{operation?.wear_per_hour ?? definition.wear_per_hour ?? 0}% / hour</dd></div>
            </dl>
            <section className="labour-coverage" data-status={staffing.status} aria-label={`${definition.short_name} labour coverage`}>
              <header>
                <UsersRound aria-hidden="true" />
                <span>
                  <small>Labour coverage</small>
                  <strong>{staffing.status}</strong>
                </span>
                {staffing.status === "uncovered" && <AlertTriangle aria-label="Required post uncovered" />}
              </header>
              <dl>
                <div><dt>Preferred</dt><dd>{staffing.preferred_worker_ids.length}</dd></div>
                <div><dt>Active</dt><dd>{staffing.active_worker_ids.length}</dd></div>
                <div><dt>Relief</dt><dd>{staffing.relief_worker_ids.length}</dd></div>
                <div><dt>Minimum</dt><dd>{staffing.minimum_staff}</dd></div>
              </dl>
              {reliefWorkers.length > 0 && <p><strong>Covering now</strong>{reliefWorkers.map((person) => person.name).join(", ")}</p>}
              <fieldset className="labour-priority-picker">
                <legend>Labour priority</legend>
                {(["critical", "essential", "normal", "deferred"] as LabourPriority[]).map((value) => (
                  <button
                    type="button"
                    key={value}
                    aria-label={`${value} labour priority`}
                    aria-pressed={labourPriority === value}
                    disabled={busy || labourPriority === value}
                    onClick={() => onCommand({ type: "set_room_operation", space_id: space.id, labour_priority: value })}
                  >
                    {value}
                  </button>
                ))}
              </fieldset>
              <div className="labour-policy-row">
                <label className="minimum-staff-control">
                  <span>Minimum active staff</span>
                  <select
                    aria-label="Minimum active staff"
                    disabled={busy}
                    value={Math.min(capacity, minimumStaff)}
                    onChange={(event) => onCommand({ type: "set_room_operation", space_id: space.id, minimum_staff: Number(event.currentTarget.value) })}
                  >
                    {Array.from({ length: capacity + 1 }, (_, value) => <option value={value} key={value}>{value}</option>)}
                  </select>
                </label>
                <label className="auto-cover-toggle">
                  <input
                    type="checkbox"
                    checked={autoCover}
                    disabled={busy}
                    onChange={() => onCommand({ type: "set_room_operation", space_id: space.id, auto_cover: !autoCover })}
                  />
                  <span><strong>Automatic relief</strong><small>Borrow eligible people from lower-priority work.</small></span>
                </label>
              </div>
            </section>
            {space.room.type === "dormitory" && operation?.rest && (
              <section className="dormitory-occupancy" aria-label="Dormitory bed occupancy">
                <header><Moon aria-hidden="true" /><span><small>Automatic rest</small><strong>{operation.rest.occupant_ids.length}/{operation.rest.bed_capacity} bunks occupied</strong></span></header>
                <dl>
                  <div><dt>In bunks</dt><dd>{operation.rest.occupant_ids.length ? nameList(operation.rest.occupant_ids) : "None"}</dd></div>
                  <div><dt>Rough sleeping</dt><dd>{operation.rest.rough_sleeper_ids.length ? nameList(operation.rest.rough_sleeper_ids) : "None"}</dd></div>
                </dl>
                {operation.rest.rough_sleeper_ids.length > 0 && <p className="warning-copy"><AlertTriangle aria-hidden="true" />Rough sleepers recover at one-quarter bunk speed and gain no rest morale.</p>}
              </section>
            )}
            {space.room.type === "infirmary" && operation?.treatment && (
              <section
                className="infirmary-treatment"
                aria-label="Infirmary automatic treatment"
              >
                <header>
                  <HeartPulse aria-hidden="true" />
                  <span>
                    <small>Automatic care</small>
                    <strong>
                      {totalPatientCount > 0
                        ? `${totalPatientCount} need care · ${admittedPatientIds.length}/${operation.treatment.bed_capacity ?? [1, 2, 4][["compact", "standard", "expanded"].indexOf(tier)]} beds occupied`
                        : "No one needs treatment"}
                    </strong>
                  </span>
                </header>
                <p>
                  Injured people at home enter this queue automatically; they do
                  not need to be assigned here. Keep the room enabled, powered,
                  staffed by a medic, and supplied with Medicine.
                </p>
                <dl>
                  <div>
                    <dt>Admitted patients</dt>
                    <dd>
                      {admittedPatientIds.length > 0 ? nameList(admittedPatientIds) : "None"}
                    </dd>
                  </div>
                  <div>
                    <dt>Waiting patients</dt>
                    <dd>
                      {waitingPatientIds.length > 0 ? nameList(waitingPatientIds) : "Clear"}
                    </dd>
                  </div>
                  <div>
                    <dt>Medics on duty</dt>
                    <dd>
                      {operation.treatment.active_worker_ids.length > 0
                        ? operation.treatment.active_worker_ids
                            .map((id) => state.survivors.find((person) => person.id === id)?.name ?? "Unknown medic")
                            .join(", ")
                        : "None active"}
                    </dd>
                  </div>
                  <div>
                    <dt>Care capacity</dt>
                    <dd>{operation.treatment.care_capacity_per_hour} points / hour</dd>
                  </div>
                  <div>
                    <dt>Medicine</dt>
                    <dd>
                      {formatQuantity(state.resources.medicine)} available · {formatQuantity(operation.treatment.medicine_per_care_point)} per point
                    </dd>
                  </div>
                </dl>
                <p className="treatment-effect">
                  Each care point restores 1 health and eases the first active
                  injury by 2 severity. Away teams cannot be treated until they return.
                </p>
              </section>
            )}
            {(operation?.power_demand ?? definition.power_demand_by_tier?.[tier] ?? 0) > 0 && (
              <fieldset className="power-priority-picker">
                <legend>Power priority</legend>
                {(["critical", "normal", "low"] as PowerPriority[]).map((value) => (
                  <button
                    type="button"
                    key={value}
                    aria-pressed={priority === value}
                    disabled={busy || priority === value}
                    onClick={() => onCommand({ type: "set_room_operation", space_id: space.id, power_priority: value })}
                  >
                    {value}
                  </button>
                ))}
              </fieldset>
            )}
            {operation && (
              Object.keys(operation.estimated_inputs_per_hour).length > 0
              || Object.keys(operation.estimated_outputs_per_hour).length > 0
            ) && (
              <div className="operation-recipe">
                <strong>Estimated hourly cycle</strong>
                <div>
                  {(Object.entries(operation.estimated_inputs_per_hour) as Array<[ResourceKey, number]>).map(([resource, amount]) => (
                    <span className="input" key={`input-${resource}`}>−{formatQuantity(amount)} {catalog.resources[resource].name}</span>
                  ))}
                  {(Object.entries(operation.estimated_outputs_per_hour) as Array<[ResourceKey, number]>).map(([resource, amount]) => (
                    <span className="output" key={`output-${resource}`}>+{formatQuantity(amount)} {catalog.resources[resource].name}</span>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}
        {space.room.status === "operational" && expansionFootprint && expansionTier && (
          <section className="room-expansion" aria-label={`Expand ${definition.short_name}`}>
            <h3><Plus size={14} />Expand room</h3>
            <p>Use reserved chamber bays to increase this room’s capability. Expansion disrupts the room while construction is underway.</p>
            <fieldset className="blueprint-picker">
              <legend>Expansion footprint</legend>
              {expansionBlueprints.map((footprint) => {
                const key = footprintKey(footprint);
                const optionTier = roomTierForFootprint(footprint);
                return (
                  <label key={key}>
                    <input
                      type="radio"
                      name={`expand-${space.id}`}
                      checked={footprintKey(expansionFootprint) === key}
                      onChange={() => setSelectedExpansion(key)}
                      aria-label={footprintLabel(footprint)}
                    />
                    <span><strong>{footprintLabel(footprint)}</strong><small>{definition.tier_effects?.[optionTier] ?? `${roomWorkerCapacity(definition, optionTier)} useful workers`}</small></span>
                  </label>
                );
              })}
            </fieldset>
            <p className="room-choice-benefit"><strong>{expansionTier} result</strong>{definition.tier_effects?.[expansionTier] ?? definition.description} · capacity {roomWorkerCapacity(definition, expansionTier)} · {formatDuration(expansionSeconds)}</p>
            <Cost cost={expansionCost} catalog={catalog} resources={state.resources} />
            <WorkerPicker state={state} survivors={available} selected={validWorkers} skill={definition.skill} onChange={setWorkers} />
            <button className="action-button" disabled={busy || !expansionAffordable || validWorkers.length === 0} onClick={() => onCommand({ type: "expand_room", space_id: space.id, footprint: expansionFootprint, worker_ids: validWorkers })}>
              <Plus size={16} />Expand {definition.short_name} <ArrowRight size={15} />
            </button>
          </section>
        )}
        {supportsInstallations && space.room.status === "operational" && installationLevel < installationCap && (
          <section className="room-upgrade" aria-label={`Upgrade ${definition.short_name}`}>
            <h3><Hammer size={14} />Upgrade to {installationLevelLabel(targetInstallationLevel)}</h3>
            <p>
              {definition.installation_effects_by_level?.[targetInstallationLevel]?.summary
                ?? `Raises effective output to ${definition.installation_output_percent_by_level?.[targetInstallationLevel] ?? 100}%.`}
              {" "}The room goes offline and its current staff are released while work is underway.
            </p>
            <dl className="upgrade-facts">
              <div><dt>Output</dt><dd>{definition.installation_output_percent_by_level?.[targetInstallationLevel] ?? 100}%</dd></div>
              <div><dt>Added load</dt><dd>+{definition.installation_power_delta_by_level?.[targetInstallationLevel] ?? 0}</dd></div>
              <div><dt>Wear</dt><dd>{definition.installation_wear_percent_by_level?.[targetInstallationLevel] ?? 100}%</dd></div>
              <div><dt>Work time</dt><dd>{upgradeSeconds == null ? "Calculated at start" : formatDuration(upgradeSeconds)}</dd></div>
            </dl>
            {Object.keys(upgradeCost).length > 0 && <Cost cost={upgradeCost} catalog={catalog} resources={state.resources} />}
            {upgradeGate && <p className="warning-copy"><AlertTriangle size={14} />{upgradeGate}</p>}
            <WorkerPicker state={state} survivors={available} selected={validWorkers} skill={definition.skill} max={4} onChange={setWorkers} />
            <button
              className="action-button"
              disabled={busy || !upgradeAffordable || validWorkers.length === 0 || Boolean(upgradeGate)}
              onClick={() => onCommand({ type: "upgrade_room", space_id: space.id, worker_ids: validWorkers })}
            >
              <Hammer size={16} />Begin {installationLevelLabel(targetInstallationLevel)} upgrade <ArrowRight size={15} />
            </button>
          </section>
        )}
        {supportsInstallations && space.room.status === "operational" && installationLevel >= installationCap && (
          <p className="installation-cap-note">This {tier} footprint has reached its {installationLevelLabel(installationCap)} installation ceiling. Expand the room to unlock further upgrades.</p>
        )}
        {space.room.status === "damaged" && (
          <>
            <Cost cost={repairCost} catalog={catalog} resources={state.resources} />
            <WorkerPicker state={state} survivors={available} selected={validWorkers} skill="engineering" onChange={setWorkers} />
            <button className="action-button" disabled={busy || !repairAffordable || validWorkers.length === 0} onClick={() => onCommand({ type: "repair_room", space_id: space.id, worker_ids: validWorkers })}>
              <Wrench size={16} />Begin repairs <ArrowRight size={15} />
            </button>
          </>
        )}
        {space.room.status === "operational" && space.room.condition < 100 && (
          <details className="room-maintenance">
            <summary><Wrench size={14} />Preventive maintenance</summary>
            <p>Restore full condition before wear forces an expensive shutdown.</p>
            <Cost cost={maintenanceCost} catalog={catalog} resources={state.resources} />
            <WorkerPicker state={state} survivors={available} selected={validWorkers} skill="engineering" max={4} onChange={setWorkers} />
            <button
              className="secondary-button"
              disabled={busy || !maintenanceAffordable || validWorkers.length === 0}
              onClick={() => onCommand({ type: "maintain_room", space_id: space.id, worker_ids: validWorkers })}
            >
              <Wrench size={15} />Maintain {definition.short_name}
            </button>
          </details>
        )}
        {space.room.status === "operational" && capacity > 0 && (
          <div className="room-roster">
            <h3><UserRound size={14} />Preferred duties · {preferredWorkers.length}/{capacity}</h3>
            {preferredWorkers.map((survivor) => (
              <button key={survivor.id} onClick={() => onCommand({ type: "clear_survivor_duty", survivor_id: survivor.id })} title="Clear preferred duty">
                <SurvivorPortrait survivor={survivor} detail="thumb" />{survivor.name}<small>{survivor.skills[definition.skill]}/5 {definition.skill} · {activeWorkerIds.has(survivor.id) ? "active" : survivorActivityLabel(state, survivor)}</small><i>x</i>
              </button>
            ))}
            {reliefWorkers.map((survivor) => (
              <div className="relief-worker" key={`relief-${survivor.id}`}>
                <SurvivorPortrait survivor={survivor} detail="thumb" /><span>{survivor.name}<small>Temporary relief · returns to their prior work automatically</small></span>
              </div>
            ))}
            {preferredWorkers.length < capacity && availableForDuty.slice(0, 3).map((survivor) => (
              <button className="empty-slot" key={survivor.id} disabled={busy} onClick={() => onCommand({ type: "assign_room", space_id: space.id, survivor_id: survivor.id })}>
                <Plus size={14} />{survivor.name}<small>{survivor.skills?.[definition.skill] ?? 0}/5 {definition.skill}</small>
              </button>
            ))}
          </div>
        )}
        {space.room.type !== "entrance" && space.room.status !== "expanding" && (
          <details className="room-decommission">
            <summary><Recycle size={14} />Repurpose this chamber</summary>
            <p>Dismantling permanently removes this room and its contents. Some structural scrap is recovered when the work finishes.</p>
            <WorkerPicker state={state} survivors={available} selected={validWorkers} skill="engineering" max={4} onChange={setWorkers} />
            <button className="secondary-button destructive-button" disabled={busy || validWorkers.length === 0} onClick={() => onCommand({ type: "decommission_room", space_id: space.id, worker_ids: validWorkers })}>
              <Recycle size={15} />Dismantle {definition.short_name}
            </button>
          </details>
        )}
      </aside>
    );
  }

  if (space.state === "sealed") {
    const footprint = spaceFootprint(space);
    return (
      <aside id="space-inspector" className="inspector panel-surface">
        <div className="inspector-kicker"><HardHat size={14} />Unopened space</div>
        <h2>{space.material}</h2>
        <p>Soundings suggest a {footprint.width} × {footprint.height} structural footprint. The estimate may change once the wall is opened.</p>
        <FootprintFacts space={space} />
        <dl className="space-facts"><div><dt>Excavation resistance</dt><dd>{space.hardness < 0.9 ? "Soft" : space.hardness > 1.15 ? "Hard" : "Moderate"}</dd></div><div><dt>Known hazard</dt><dd>{space.hazard ?? "None observed"}</dd></div></dl>
        <WorkerPicker state={state} survivors={available} selected={validWorkers} skill="engineering" onChange={setWorkers} />
        <button className="action-button" disabled={busy || validWorkers.length === 0} onClick={() => onCommand({ type: "excavate", space_id: space.id, worker_ids: validWorkers })}>
          <Hammer size={16} />Start excavation <ArrowRight size={15} />
        </button>
      </aside>
    );
  }

  const footprint = spaceFootprint(space);
  const rooms = Object.entries(catalog.rooms).filter(
    ([key, definition]) =>
      key !== "entrance" &&
      availableBlueprints(definition, space).length > 0 &&
      (!definition.locked_by || state.research.completed.includes(definition.locked_by)),
  );
  return (
    <aside id="space-inspector" className="inspector panel-surface room-choice-inspector">
      <div className="inspector-kicker"><HardHat size={14} />Cleared chamber · {footprint.width} × {footprint.height}</div>
      <h2>Give this space a purpose</h2>
      <p>Construction reserves its materials immediately. Any survivor can help, but relevant skill makes the work safer and faster.</p>
      <FootprintFacts space={space} />
      <div className="room-choice-list">
        {rooms.map(([key, definition]) => {
          const blueprints = availableBlueprints(definition, space);
          const selected = blueprints.find((candidate) => footprintKey(candidate) === selectedBlueprints[key]) ?? blueprints[0]!;
          const selectedTier = roomTierForFootprint(selected);
          const selectedCost = buildCost(definition, selected);
          const affordable = (Object.entries(selectedCost) as Array<[ResourceKey, number]>).every(([resource, amount]) => state.resources[resource] >= amount);
          const capacity = roomWorkerCapacity(definition, selectedTier);
          const benefit = definition.tier_effects?.[selectedTier];
          const minimum = roomMinimumUnits(definition);
          return (
            <details key={key}>
              <summary><span>{definition.short_name}<small>{minimum} {minimum === 1 ? "bay" : "bays"} minimum · {footprintLabel(selected)} · {formatDuration(buildSeconds(definition, selected))}</small></span><em>{definition.skill}</em></summary>
              <p>{definition.description}</p>
              <fieldset className="blueprint-picker">
                <legend>Choose footprint</legend>
                {blueprints.map((blueprint) => {
                  const blueprintKey = footprintKey(blueprint);
                  const blueprintTier = roomTierForFootprint(blueprint);
                  return (
                    <label key={blueprintKey}>
                      <input
                        type="radio"
                        name={`build-${space.id}-${key}`}
                        checked={footprintKey(selected) === blueprintKey}
                        onChange={() => setSelectedBlueprints((current) => ({ ...current, [key]: blueprintKey }))}
                        aria-label={footprintLabel(blueprint)}
                      />
                      <span><strong>{footprintLabel(blueprint)}</strong><small>{definition.tier_effects?.[blueprintTier] ?? `${roomWorkerCapacity(definition, blueprintTier)} useful workers`}</small></span>
                    </label>
                  );
                })}
              </fieldset>
              <p className="room-choice-benefit"><strong>{selectedTier} result</strong>{benefit ?? definition.description} · capacity {capacity}</p>
              <Cost cost={selectedCost} catalog={catalog} resources={state.resources} />
              <WorkerPicker state={state} survivors={available} selected={validWorkers} skill={definition.skill} onChange={setWorkers} />
              <button disabled={busy || !affordable || validWorkers.length === 0} onClick={() => onCommand({ type: "build_room", space_id: space.id, room_type: key, footprint: selected, worker_ids: validWorkers })}>
                Build {definition.short_name}<ArrowRight size={14} />
              </button>
            </details>
          );
        })}
      </div>
    </aside>
  );
}
